import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaLock,
  FaShieldAlt,
  FaBolt,
  FaKey,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { forgotPassword } from "../../api/api";
import { requestOtp } from "../../utils/otpUtils";
import AuthLayout from "../layout/AuthLayout";
import AuthBranding from "./AuthBranding";
import FormInput from "./FormInput";
import GetOtpBtn from "./GetOtpBtn";
import "../custom.css";

const forgotFeatures = [
  { icon: <FaShieldAlt size={22} color="#6FE6B8" />, text: "Secure Process" },
  { icon: <FaBolt size={22} color="#6FE6B8" />, text: "Quick Recovery" },
  { icon: <FaKey size={22} color="#6FE6B8" />, text: "New Password" },
];

const Forgot = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  const handleGetOtp = () => {
    requestOtp({
      email,
      setLoading: setOtpLoading,
      setTimer: setOtpTimer,
    });
  };

  useEffect(() => {
    let timer;
    if (otpTimer > 0) {
      timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpTimer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !otp || !password || !confirmPassword) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      const response = await forgotPassword({
        email,
        password,
        confirmpassword : confirmPassword,
        otp,
      });

      if (
        response?.success ||
        response?.data?.success ||
        response?.status === 200 ||
        response?.status === true ||
        response?.status === "success"
      ) {
        toast.success(
          response?.message || response?.data?.message || "Password reset successful!"
        );
        setTimeout(() => {
          navigate("/signin");
        }, 1800);
      } else {
        toast.error(
          response?.message || response?.data?.message || "Failed to reset password"
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Error resetting password");
    } finally {
      setLoading(false);
    }
  };

  const contentPanel = (
    <AuthBranding
      accentText="Reset"
      titleSuffix="Password"
      subtitle="Enter your registered email address and verification OTP to set your new password."
      features={forgotFeatures}
    />
  );

  const getOtpBtn = (
    <GetOtpBtn
      onClick={handleGetOtp}
      loading={otpLoading}
      timer={otpTimer}
    />
  );

  const formPanel = (
    <>
      <div className="auth-heading">
        <h2>Forgot Password</h2>
        <p className="form-subtitle">
          Enter your registered email to receive a password reset OTP
        </p>
      </div>

      <form className="auth-form" onSubmit={handleSubmit}>
        {/* Email */}
        <FormInput
          label="Email Address"
          type="email"
          placeholder="Enter Registered Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          icon={<FaEnvelope />}
          required
          extraElement={getOtpBtn}
        />

        {/* OTP Input */}
        <FormInput
          label="Enter OTP"
          type="text"
          placeholder="Enter 6-digit OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          icon={<FaKey />}
          required
        />

        {/* New Password */}
        <FormInput
          label="New Password"
          type="password"
          placeholder="Enter New Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          icon={<FaLock />}
          required
          className="mb-3"
        />

        {/* Confirm Password */}
        <FormInput
          label="Confirm Password"
          type="password"
          placeholder="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          icon={<FaLock />}
          required
          className="mb-4"
        />

        {/* Submit */}
        <button
          type="submit"
          className="btn btn-signin w-100"
          disabled={loading}
        >
          {loading ? "Resetting..." : "Reset Password"}
        </button>
      </form>

      <div className="auth-footer mt-4 text-center">
        <p>
          Remembered your password?{" "}
          <Link to="/signin" className="auth-link">
            Sign In
          </Link>
        </p>
      </div>
    </>
  );

  return <AuthLayout contentPanel={contentPanel} formPanel={formPanel} />;
};

export default Forgot;
